import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "../../Layout/PageHeader";

import toast, { Toaster } from "react-hot-toast";
import CustomLoader from "../../Components/GlobalLoader/CustomLoader";
import TabProgressBar from "../../Shared/Tab/TabProgressBar";
import AccountBasicInfo from "./AccountBasicInfo";
import Address from "./Address";
import Configuration from "./Configuration";
import { tabGroupInterface } from "./model/accountForm.model";
import {
  defaultBasicInfo,
  defaultCompanyAccountConfiguration,
  defaultCompanyAddress
} from "./model/CompanyAccount.type";
import {
  addressLookups,
  basicInfoLookups,
  createNewCompanyAcc,
  getAccountDataById,
  uomLookups,
  updateAccountById
} from "./service/accountOverview.services";

interface LoaderState {
  accountCreateLoader: boolean;
  accountEditLoader: boolean;
}

export default function AccountAddEdit() {
  const { id } = useParams();
  const pageName = "Company Account";
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);

  const [basicInfo, setBasicInfo] = useState<any | null>();
  const [compAddress, setCompAddress] = useState<any>();
  const [config, setConfig] = useState<any>();
  const [isFormSubmited, setFormIsSubmited] = useState(false);
  const [loaderState, setLoaderState] = useState<LoaderState>({
    accountCreateLoader: false,
    accountEditLoader: false,
  });
  const tabGroup: tabGroupInterface = {
    0: "basicInfoTab",
    1: "addressTab",
    2: "configTab",
  };
  const [accountCode, setAccountCode] = useState<string>("");
 
  const [lookupData, setLookupData] = useState({
    accountBasicInfo: {
      accountType: [],
      accountSource: [],
      environmentType: [],
    },
    accountAddress: {
      countries: [],
      timezone: [],
    },
    configuration: {
      uom_system: [],
    },
  });


  useEffect(() => {
    const fetchAccountData = async () => {
      if (id) {
        try {
          const accountData = await getAccountDataById(id);
          setBasicInfo(accountData.basicInfo);
          setCompAddress(accountData.companyAddress);
          setConfig(accountData.companyConfig);
          setAccountCode(accountData.basicInfo.code)
        } catch (error) {
          console.error("Failed to fetch account data:", error);
        }
      } else {
        // Set defaults for new account
        setBasicInfo({ ...defaultBasicInfo });
        setCompAddress({ ...defaultCompanyAddress });
        setConfig({ ...defaultCompanyAccountConfiguration });
      }
    };
    fetchAccountData();
  }, [id]);

  useEffect(() => {
    getLookUpForSelectedTab(currentTab)
  
  }, [currentTab]);

  useEffect(() => {
    if (isFormSubmited) {
      save();
    }
  }, [isFormSubmited]);

  const createAccount = async (payload: any) => {
    try {
      setLoaderState(prev => ({ ...prev, accountCreateLoader: true }));
      const response = await createNewCompanyAcc(payload);
      
      if (response.status === 201) {
        navigate("/account/company-account/grid-view");
      } else if (response.status === 400) {
        setFormIsSubmited(false)
        const errorMessage = response.error.detail.code[0];
        toast.error(errorMessage);
        setCurrentTab(0);
      }
    } catch (error) {
      setFormIsSubmited(false)
      console.error("Failed to create company account:", error);
      toast.error("Failed to create company account");
    } finally {
      setLoaderState(prev => ({ ...prev, accountCreateLoader: false }));
    }
  };

  const updateAccount = async (payload: any, id: string) => {
    try {
      setLoaderState(prev => ({ ...prev, accountEditLoader: true }));
      const response = await updateAccountById(id, payload);
      
      if (response.status === 200) {
        navigate("/account/company-account/grid-view");
      } else if (response.status === 400) {
        setFormIsSubmited(false)
        const errorMessage = response.error.detail.code[0];
        toast.error(errorMessage);
        setCurrentTab(0);
      }
    } catch (error) {
      setFormIsSubmited(false)
      console.error("Failed to update company account:", error);
      toast.error("Failed to update company account");
    } finally {
      setLoaderState(prev => ({ ...prev, accountEditLoader: false }));
    }
  };

  // Get Lookup data according to the selected tab
  const getLookUpForSelectedTab = async (tab: number) => {
    try {
      const tabKey = tabGroup[tab];

      switch (tabKey) {
        case "basicInfoTab":
          basicInfoLookups()
            .then((response) => {
              setLookupData((prev) => ({
                ...prev,
                accountBasicInfo: {
                  accountType: response[0],
                  accountSource: response[1],
                  environmentType: response[2],
                },
              }));
            })
            .catch((error) => {
              console.error("Failed to fetch basic info lookups:", error);
            });
          break;

        case "addressTab":
          addressLookups()
            .then((response) => {
              setLookupData((prev) => ({
                ...prev,
                accountAddress: {
                  countries: response[0],
                  timezone: response[1],
                },
              }));
            })
            .catch((error) => {
              console.error("Failed to fetch basic info lookups:", error);
            });

          break;

        case "configTab":
          uomLookups()
            .then((response) => {
              setLookupData((prev) => ({
                ...prev,
                configuration: {
                  uom_system: response[0],
                },
              }));
            })
            .catch((error) => {
              console.error("Failed to fetch basic info lookups:", error);
            });
          break;

        default:
          return;
      }
    } catch (error) {
      toast.error("Failed to Fetch Lookup Data");
    }
  };

  //Function To handlePayload for hitting the functions of Update and Create account respectively
  const save = async () => {
    try {
      let updatedData = {
        ...basicInfo,
        ...compAddress,
        ...config,
      };

      if (id) {
         updateAccount(updatedData, id);
      } else {
        createAccount(updatedData);
      }
    } catch (error) {
      toast.error("Failed to save configuration");
    }
  };

  const handleNext = () => {
    setCurrentTab((prevTab) =>
      Math.min(prevTab + 1, TabProgressBarData.length - 1)
    );
    const updatedCompletedTabs = [...completedTabs];
    updatedCompletedTabs[currentTab] = true;
    setCompletedTabs(updatedCompletedTabs);
  };

  const validateIsEditScreen = () => {
    if (id) {
      return true;
    } else {
      return false;
    }
  };


  const TabProgressBarData = [
    {
      step: 1,
      label: "Basic Information",
      content: (
        <AccountBasicInfo
        basicInfo={basicInfo}
          handleNext={handleNext}
          setBasicInfo={setBasicInfo}
          basicInfoLookUpData={lookupData.accountBasicInfo}
        />
      ),
    },
    {
      step: 2,
      label: "Address",
      content: (
        <Address
          compAddress={compAddress}
          setCompAddress={setCompAddress}
          handleNext={handleNext}
          addressLookUpData={lookupData.accountAddress}
        />
      ),
    },
    {
      step: 3,
      label: "Configuration",
      content: (
      
        <Configuration
          handleNext={handleNext}
          config={config}
          setConfig={setConfig}
          configLookupData={lookupData.configuration}
          isEditScreen={validateIsEditScreen()}
          setFormIsSubmited={setFormIsSubmited}
        />
      ),
    },
  ];
  const [completedTabs, setCompletedTabs] = useState(
    Array(TabProgressBarData.length).fill(false)
  );

  return (
    <>
      {(loaderState.accountCreateLoader || loaderState.accountEditLoader) && (
        <CustomLoader
          message={
            loaderState.accountEditLoader
              ? "Please wait while updating company account"
              : "Please wait while creating company account"
          }
        />
      )}
      <PageHeader
        addUrl="/account/company-account/add"
        listUrl="/account/company-account/list-view"
        gridUrl="/account/company-account/grid-view"
        name={pageName}
        viewType={true}
      />
      <Toaster />
      <div className="clearfix"></div>

      <h5 className="text-start mt-1 mb-3">{id ? `Edit Company Account (${accountCode})` : "Create Company Account"}
      </h5>
      <TabProgressBar
        tabs={TabProgressBarData}
        handleNext={handleNext}
        currentTab={currentTab}
      />
    </>
  );
}
